import { memo, useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Header } from '@shared/components/Header'
import { Tabs } from '@shared/components/MaterialUIEnhancements/Tabs'
import { Content } from '@shared/layouts'
import { NavigationTabLink } from 'components/LinkedMUIComponents/NavigationTabLink'
import { ActionsMenu } from 'domains/Workspaces/Images/Image'
import { useImageObjectUpdateMutation } from 'hooks/mutations/content'
import { imageBlobsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Images/Image/Blobs'
import { imageRoute, rawRoute } from 'pages/NWPlatform/Workspaces/Workspace/Images/Image/Routing'
import { imageObjectQueryKeys } from 'services/queryKeys'

const StructureRaw = () => {
  const { t } = useTranslation(['common', 'pages', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { imageId } = imageRoute.useParams()

  const imageQuery = useQuery(imageObjectQueryKeys.detail({ imageId }))

  const { mutate: updateImage, ...imageUpdateMutation } = useImageObjectUpdateMutation()

  const handleUpdateImageName = useCallback(
    (title: string, closeCallback: () => void) => {
      updateImage(
        {
          imageId: imageId,
          imageObjectEdit: { name: title },
        },
        {
          onError: () => enqueueSnackbar(t('pages:Workspace.updateError')),
          onSettled: closeCallback,
        },
      )
    },
    [t, updateImage, imageId, enqueueSnackbar],
  )

  return (
    <Content
      HeaderSlot={
        <Header
          title={imageQuery.data?.name ?? ''}
          isLoading={imageQuery.isLoading}
          isSaving={imageUpdateMutation.isPending}
          onSave={handleUpdateImageName}
          TabNavigation={
            <Tabs>
              <NavigationTabLink from={imageRoute.to} to={rawRoute.to}>
                {t('config')}
              </NavigationTabLink>
              <NavigationTabLink from={imageRoute.to} to={imageBlobsOverviewRoute.to}>
                {t('entity:content.blob', { count: 2 })}
              </NavigationTabLink>
            </Tabs>
          }
          Actions={<ActionsMenu />}
        />
      }
    />
  )
}

export const Structure = memo(StructureRaw)
